import axios from "axios";
import { msalInstance, startReauthRedirect } from "../authConfig";

const apiBase = import.meta.env.VITE_API_URL || '';

export const apiClient = axios.create({
  baseURL: apiBase,
});

// Inject MSAL token or OOB session token into every request
apiClient.interceptors.request.use(async (config) => {
  const requestUrl = (config.url || '').toLowerCase();
  const isOobRequest = requestUrl.includes('/api/oob/') || requestUrl.includes('/api/admin/oob');

  // Always add session GUID for activity logging when available.
  let account = msalInstance.getActiveAccount();

  if (!account) {
    const allAccounts = msalInstance.getAllAccounts();
    if (allAccounts.length > 0) {
      account = allAccounts[0];
      msalInstance.setActiveAccount(account);
    }
  }

  if (!account) {
    console.warn("No active MSAL account");
    // For OOB/public flows, allow requests to continue without bearer auth.
    if (!isOobRequest) {
      return config;
    }
  }

  // Add session GUID for activity logging
  const sessionGuid = localStorage.getItem('sessionGuid');
  if (sessionGuid) {
    config.headers['X-Session-Guid'] = sessionGuid;
  }

  // Only attach OOB session headers to OOB APIs.
  if (isOobRequest) {
    const adminSessionStr = localStorage.getItem('oob-admin-session');
    if (adminSessionStr) {
      try {
        const adminSession = JSON.parse(adminSessionStr);
        const expiresAt = new Date(adminSession.sessionExpiresAt);

        if (expiresAt > new Date()) {
          config.headers['X-OOB-Session'] = adminSession.sessionToken;
        } else {
          localStorage.removeItem('oob-admin-session');
        }
      } catch (e) {
        console.error("Failed to parse OOB admin session:", e);
        localStorage.removeItem('oob-admin-session');
      }
    }

    const oobSessionStr = localStorage.getItem('oob-session');
    if (oobSessionStr && !config.headers['X-OOB-Session']) {
      try {
        const oobSession = JSON.parse(oobSessionStr);
        const expiresAt = new Date(oobSession.sessionExpiresAt);

        if (expiresAt > new Date()) {
          config.headers['X-OOB-Session'] = oobSession.sessionToken;
        } else {
          localStorage.removeItem('oob-session');
        }
      } catch (e) {
        console.error("Failed to parse OOB session:", e);
        localStorage.removeItem('oob-session');
      }
    }
  }

  // Try to add MSAL token for M365-authenticated requests
  try {
    if (account) {
      const result = await msalInstance.acquireTokenSilent({
        scopes: ["api://f3391e09-a7dc-4866-864d-893fb9b351ac/.default"],
        account,
      });

      config.headers.Authorization = `Bearer ${result.accessToken}`;
    }
  } catch (tokenError: any) {
    const code = tokenError?.errorCode || tokenError?.code || "";
    const message = (tokenError?.message || "").toString().toLowerCase();
    const needsInteraction =
      code === "interaction_required" ||
      code === "login_required" ||
      code === "consent_required" ||
      code === "no_tokens_found" ||
      message.includes("interaction_required") ||
      message.includes("login_required") ||
      message.includes("consent_required");

    // Keep OOB/public requests usable without forcing a redirect.
    if (needsInteraction && !isOobRequest) {
      await startReauthRedirect(tokenError);
      return Promise.reject(tokenError);
    }

    console.debug("No MSAL token available; proceeding as anonymous/OOB request");
  }
  
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const requestUrl = (error?.config?.url || '').toLowerCase();
    const isOobRequest = requestUrl.includes('/api/oob/') || requestUrl.includes('/api/admin/oob');
    if (error?.response?.status === 401 && !isOobRequest) {
      await startReauthRedirect(error);
    }

    return Promise.reject(error);
  }
);

export default apiClient;